import { useMemo } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate } from '@/ui/router';
import { VOCABULARY } from '@/domain/vocabulary';
import { countdownLabel, daysUntil, MIDTERM_PARTS, MIDTERM_TOTAL } from '@/domain/midterm';
import { CountChip, EmptyState } from '@/ui/components/bits';
import { isDue } from '@/domain/leitner';
import type { Session } from '@/domain/types';

export function HomeScreen() {
  const { sessions, items, notebook, progress, formulas, constants } = useAppState();

  const openMistakes = notebook.filter((e) => e.clearedAt === null).length;
  const referenceCount = formulas.length + constants.length;
  const dueCount = useMemo(() => {
    const now = Date.now();
    return items.filter((i) => isDue(progress.get(i.id), now)).length;
  }, [items, progress]);

  const counts = useMemo(() => {
    const map = new Map<string, { cards: number; questions: number }>();
    for (const item of items) {
      const entry = map.get(item.sessionId) ?? { cards: 0, questions: 0 };
      if (item.type === 'flashcard') entry.cards += 1;
      else entry.questions += 1;
      map.set(item.sessionId, entry);
    }
    return map;
  }, [items]);

  const classes = sessions.filter((s) => s.kind === 'class');
  const chapters = sessions.filter((s) => s.kind === 'chapter');
  const textbook = sessions.filter((s) => s.kind === 'textbook');

  if (sessions.length === 0) {
    return (
      <div className="stack">
        <EmptyState icon="🧪" title="No classes yet">
          Give your notes to a Claude chat with the instructions from Add class, then paste the
          import block it replies with.
        </EmptyState>
        <button className="btn btn--primary btn--block" onClick={() => navigate('/add')}>
          Add class
        </button>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="spread">
        <h1>Study</h1>
        <button className="btn btn--sm" onClick={() => navigate('/add')}>
          + Add class
        </button>
      </div>

      {/* The one thing that matters until it is over, and then it goes away. */}
      {daysUntil() >= 0 ? (
        <button className="mode-card midterm__card" onClick={() => navigate('/midterm')}>
          <span className="mode-card__title">Mid-term · {countdownLabel()}</span>
          <span className="mode-card__sub">
            Chapters 1–6, {MIDTERM_PARTS.length} parts, {MIDTERM_TOTAL} marks. The paper part by
            part, weakest first.
          </span>
        </button>
      ) : null}

      <div className="grid-2">
        <button className="mode-card" onClick={() => navigate('/study', { mode: 'class' })}>
          <span className="mode-card__title">Choose class</span>
          <span className="mode-card__sub">One session at a time, cards or quiz.</span>
        </button>
        <button className="mode-card" onClick={() => navigate('/study', { mode: 'cumulative' })}>
          <span className="mode-card__title">Cumulative review</span>
          <span className="mode-card__sub">Everything, mixed and de-duplicated.</span>
        </button>
        <button className="mode-card" onClick={() => navigate('/study', { mode: 'chapter' })}>
          <span className="mode-card__title">Chapter review</span>
          <span className="mode-card__sub">
            {chapters.length ? `${chapters.length} chapter review${chapters.length === 1 ? '' : 's'}.` : 'No chapter reviews imported yet.'}
          </span>
        </button>
        <button className="mode-card" onClick={() => navigate('/quiz', { mode: 'cumulative' })}>
          <span className="mode-card__title">Quick quiz</span>
          <span className="mode-card__sub">Cumulative questions, straight in.</span>
        </button>
        {textbook.length > 0 ? (
          <button className="mode-card" onClick={() => navigate('/study', { mode: 'textbook' })}>
            <span className="mode-card__title">Textbook chapter</span>
            <span className="mode-card__sub">
              {textbook.length} chapter{textbook.length === 1 ? '' : 's'} from the book.
            </span>
          </button>
        ) : null}
      </div>

      <h2 className="section-title">Practice</h2>
      <div className="grid-2">
        <button className="mode-card" onClick={() => navigate('/exam')}>
          <span className="mode-card__title">Timed exam</span>
          <span className="mode-card__sub">Quick 10, half or full, on the clock.</span>
        </button>
        <button className="mode-card" onClick={() => navigate('/notebook')}>
          <span className="mode-card__title">Mistake notebook</span>
          <span className="mode-card__sub">
            {openMistakes > 0
              ? `${openMistakes} open ${openMistakes === 1 ? 'entry' : 'entries'}.`
              : 'Everything you miss lands here.'}
          </span>
        </button>
        <button className="mode-card" onClick={() => navigate('/problems')}>
          <span className="mode-card__title">Practice problems</span>
          <span className="mode-card__sub">Endless randomised numeric problems.</span>
        </button>
        <button className="mode-card" onClick={() => navigate('/balance')}>
          <span className="mode-card__title">Equation balancer</span>
          <span className="mode-card__sub">Balance, practise, and work redox through.</span>
        </button>
        <button className="mode-card" onClick={() => navigate('/vocab')}>
          <span className="mode-card__title">Vocabulary</span>
          <span className="mode-card__sub">
            {VOCABULARY.length} terms from the textbook glossary, defined, by chapter.
          </span>
        </button>
        <button className="mode-card" onClick={() => navigate('/sheet')}>
          <span className="mode-card__title">Cheat sheet</span>
          <span className="mode-card__sub">
            {referenceCount > 0
              ? `${referenceCount} formulas and constants, ready to print.`
              : 'Formulas and constants from your imports.'}
          </span>
        </button>
      </div>

      {dueCount > 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          {dueCount} item{dueCount === 1 ? '' : 's'} due for review right now.
        </p>
      ) : null}

      <SessionSection title="Classes" sessions={classes} counts={counts} />
      <SessionSection title="Chapter reviews" sessions={chapters} counts={counts} />
      <SessionSection title="Textbook" sessions={textbook} counts={counts} />
    </div>
  );
}

function SessionSection({
  title,
  sessions,
  counts,
}: {
  title: string;
  sessions: Session[];
  counts: Map<string, { cards: number; questions: number }>;
}) {
  if (sessions.length === 0) return null;
  return (
    <>
      <h2 className="section-title">{title}</h2>
      <ul className="list">
        {sessions.map((session) => {
          const count = counts.get(session.id) ?? { cards: 0, questions: 0 };
          return (
            <li key={session.id}>
              <button className="list-item" onClick={() => navigate('/session', { id: session.id })}>
                <div className="list-item__main">
                  <div className="list-item__title">{session.title}</div>
                  <div className="list-item__sub">{session.shortLabel}</div>
                </div>
                <CountChip>
                  {count.cards} cards · {count.questions} Q
                </CountChip>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}
